import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Send, RefreshCcw, Trash2, Cpu, Terminal as TerminalIcon } from "lucide-react";

const PROVIDERS = [
  { id: "ollama", label: "Ollama (local)" },
  { id: "lm_studio", label: "LM Studio (local)" },
  { id: "ollama_cloud", label: "Ollama Cloud" },
];

const QUICK_TASKS = [
  { label: "Summarize project notes", prompt: "Summarize my recent project notes and highlight any blockers." },
  { label: "Recon a domain", prompt: "Walk me through ethical reconnaissance steps for the domain example.com — passive only." },
  { label: "Code review", prompt: "Review this code for vulnerabilities and improvements:\n\n```\n// paste code\n```" },
  { label: "Weekly standup", prompt: "Draft a weekly standup based on tasks closed and tasks in play." },
];

export default function AIChat() {
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState("ollama");
  const [model, setModel] = useState("");
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const scrollerRef = useRef(null);

  const loadSettings = async () => {
    const { data } = await api.get("/ai/settings");
    setSettings(data);
    setProvider(data.default_provider || "ollama");
    setModel(
      data.default_provider === "lm_studio" ? (data.lm_studio_model || "")
      : data.default_provider === "ollama_cloud" ? (data.ollama_cloud_model || "")
      : (data.ollama_model || "")
    );
  };

  const loadConversations = async () => {
    const { data } = await api.get("/ai/conversations");
    setConversations(data);
  };

  const refreshModels = async (p = provider) => {
    setModelsLoading(true);
    setModelsError("");
    try {
      const { data } = await api.get(`/ai/models?provider=${p}`);
      setModels(data.models || []);
      if (data.error) setModelsError(data.error);
      if ((data.models || []).length && !model) setModel(data.models[0]);
    } catch (e) {
      setModelsError(e?.response?.data?.detail || e.message);
    }
    setModelsLoading(false);
  };

  useEffect(() => {
    loadSettings();
    loadConversations();
  }, []);

  useEffect(() => {
    refreshModels(provider);
    // eslint-disable-next-line
  }, [provider]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const loadConversation = async (id) => {
    const { data } = await api.get(`/ai/conversations/${id}`);
    setMessages(data.map((m) => ({ role: m.role, content: m.content })));
    setConversationId(id);
  };

  const deleteConversation = async (id) => {
    await api.delete(`/ai/conversations/${id}`);
    if (conversationId === id) {
      setMessages([]);
      setConversationId(null);
    }
    loadConversations();
  };

  const send = async (overridePrompt) => {
    const prompt = (overridePrompt ?? input).trim();
    if (!prompt || streaming) return;
    const userMsg = { role: "user", content: prompt };
    const assistantMsg = { role: "assistant", content: "" };
    const next = [...messages, userMsg, assistantMsg];
    setMessages(next);
    setInput("");
    setStreaming(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider,
          model,
          temperature: 0.7,
          conversation_id: conversationId,
          messages: next.filter((m) => m.content).slice(0, -1).map(({ role, content }) => ({ role, content })),
        }),
      });
      const convHeader = res.headers.get("X-Conversation-Id");
      if (convHeader) setConversationId(convHeader);

      if (!res.ok || !res.body) {
        const text = await res.text();
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: `error: ${text || res.statusText}` };
          return copy;
        });
        setStreaming(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            if (data.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: (copy[copy.length - 1].content || "") + data.delta,
                };
                return copy;
              });
            }
            if (data.error) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: `error: ${data.error}` };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: `error: ${e.message}` };
        return copy;
      });
    }
    setStreaming(false);
    loadConversations();
  };

  return (
    <div className="p-8 lg:p-12">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono text-green-400 mb-2">// ai.suite</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="ai-title">AI Terminal</h1>
          <p className="text-neutral-400 text-sm mt-2">Bring your own model. Stream the response on the felt.</p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <aside className="lg:col-span-3 space-y-4">
          <div className="panel panel-accent-green p-4">
            <div className="label-mono mb-3">// provider</div>
            <select
              data-testid="ai-provider-select"
              value={provider}
              onChange={(e) => { setProvider(e.target.value); setModel(""); }}
              className="input-shell w-full px-3 py-2 text-sm font-jet mb-3"
            >
              {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <select
                data-testid="ai-model-select"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="input-shell flex-1 px-3 py-2 text-sm font-jet"
              >
                <option value="">{modelsLoading ? "Loading…" : "Pick a model"}</option>
                {models.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button data-testid="ai-refresh-models" onClick={() => refreshModels(provider)} className="btn-ghost px-3 py-2">
                <RefreshCcw size={12} />
              </button>
            </div>
            {modelsError && <div className="mt-2 text-[11px] text-red-400 font-jet">{modelsError}</div>}
            <div className="mt-3 text-[11px] text-neutral-500 font-jet">
              Configure endpoints in <a className="text-amber-400" href="/app/settings">Settings</a>.
            </div>
          </div>

          <div className="panel p-4">
            <div className="label-mono mb-3 flex items-center gap-2"><Cpu size={12} /> quick.tasks</div>
            <div className="space-y-2">
              {QUICK_TASKS.map((t) => (
                <button
                  key={t.label}
                  data-testid={`ai-quick-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => send(t.prompt)}
                  className="w-full text-left text-xs font-jet uppercase tracking-[0.16em] px-3 py-2 border border-neutral-800 hover:border-green-500/60 hover:text-green-400 transition"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="panel p-4">
            <div className="label-mono mb-3 flex items-center justify-between">
              <span>history</span>
              <button data-testid="ai-new-chat" onClick={() => { setMessages([]); setConversationId(null); }} className="text-amber-400 hover:text-amber-300">new</button>
            </div>
            <div className="space-y-1 max-h-64 overflow-auto">
              {conversations.length === 0 && (
                <div className="text-[11px] text-neutral-500 font-jet">No conversations yet.</div>
              )}
              {conversations.map((c) => (
                <div key={c.id} className={`group flex items-center justify-between gap-2 px-2 py-2 text-xs border-l-2 ${conversationId === c.id ? "border-red-500 bg-red-500/5" : "border-transparent hover:bg-neutral-900/40"}`}>
                  <button
                    data-testid={`ai-conv-${c.id}`}
                    onClick={() => loadConversation(c.id)}
                    className="flex-1 text-left truncate font-jet"
                  >
                    {c.preview || "(empty)"}
                  </button>
                  <button data-testid={`ai-conv-delete-${c.id}`} onClick={() => deleteConversation(c.id)} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-400">
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9 panel p-0 min-h-[70vh] flex flex-col" data-testid="ai-chat-panel">
          <div className="px-4 py-3 border-b border-neutral-900 flex items-center gap-3">
            <TerminalIcon size={14} className="text-green-400" />
            <span className="label-mono">acehack@suite:~$</span>
            <span className="label-mono ml-auto">{provider}{model ? ` :: ${model}` : ""}</span>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-auto p-5 font-jet text-sm bg-black/40">
            {messages.length === 0 && (
              <div className="text-neutral-500">
                <div className="text-green-400">{"> "}session.ready</div>
                <div className="mt-2">Pick a provider, choose a model, and type below. Use a quick task to start with a template.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className="mb-4" data-testid={`ai-msg-${i}`}>
                {m.role === "user" ? (
                  <div>
                    <span className="text-amber-400">user@you{"> "}</span>
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  </div>
                ) : (
                  <div>
                    <span className="text-green-400">ai@{provider}{"> "}</span>
                    <span className="whitespace-pre-wrap text-green-300">{m.content || (streaming && i === messages.length - 1 ? "" : "")}</span>
                    {streaming && i === messages.length - 1 && <span className="terminal-cursor" />}
                  </div>
                )}
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-neutral-900 p-3 flex items-center gap-3"
            data-testid="ai-input-form"
          >
            <span className="label-mono text-green-400">{"$"}</span>
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={model ? "Type a prompt and press enter…" : "Pick a model first…"}
              disabled={!model || streaming}
              className="flex-1 bg-transparent outline-none font-jet text-sm placeholder:text-neutral-600"
            />
            <button
              type="submit"
              data-testid="ai-send-button"
              disabled={!model || streaming || !input.trim()}
              className="btn-primary btn-glitch flex items-center gap-2 py-2 px-4 text-[11px]"
            >
              <Send size={12} /> Send
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
